/**
 * The headers every edge function call carries (defect D1).
 *
 * Supabase puts a gate in front of a deployed function. When verify_jwt is on,
 * the gate reads the Authorization header, verifies it against the project JWT
 * secret, and answers 401 UNAUTHORIZED_NO_AUTH_HEADER before the function runs.
 * Sending only `apikey`, which is what both shells used to do, means every
 * call is refused and no one can sign in at all.
 *
 * So both headers go on every call:
 *
 *   apikey          the anon key, which is what PostgREST and the gate expect
 *                   as the project identifier,
 *   Authorization   the caller's own session token when they have one, and the
 *                   anon key when they do not.
 *
 * The anon key is itself a JWT signed with the project secret, so it satisfies
 * the gate without claiming to be anybody. A function that needs a real session
 * still has to check: `readClaims` in supabase/functions/_shared/logic/caller.ts
 * refuses a token whose role is `anon`, so the fallback opens no door.
 *
 * With this, both deploy modes work: verify_jwt off (the two login functions,
 * see supabase/config.toml) and verify_jwt on (translate).
 */

export type FunctionHeaderInput = {
  /** The project anon key. Safe in the bundle only because RLS is complete. */
  anonKey: string;
  /** The caller's minted session token, when they are signed in. */
  token?: string | null;
  /** Set false for a body that is not JSON. */
  json?: boolean;
};

export function functionHeaders({ anonKey, token, json = true }: FunctionHeaderInput): Record<string, string> {
  const bearer = token && token.length > 0 ? token : anonKey;
  const headers: Record<string, string> = {
    apikey: anonKey,
    Authorization: `Bearer ${bearer}`,
  };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}
